// MapLibre Style-Generator für Offline-Liguria-Karte.
//
// Wir nutzen:
//   - Basis-Vektor-Tiles im OpenMapTiles-Schema (PMTiles via Backend)
//   - Höhenlinien als separate PMTiles-Source
//   - Trail-Lines als GeoJSON-Source (dynamisch)
//
// Fallback: wenn keine PMTiles verfügbar sind, zeigen wir OSM-Raster-Tiles
// (online-only, aber gut für erstes Testen ohne die 30-min Tile-Generierung).

import type { StyleSpecification } from 'maplibre-gl';
import { DIFFICULTY_COLOR, type Difficulty } from '@adtu/shared';

// ---------------------------------------------------------------------
// "MapOut"-Style: fertiger "Liberty"-Style von OpenFreeMap.
//
// Warum fertiger Style statt selbstgebaut: die Custom-Variante mit
// OpenFreeMap Vector + maplibre-contour hatte zu viele Kompatibilitäts-
// probleme (Tile-Parsing-Fehler, verschiedene Protokoll-Versionen).
// Liberty ist professionell kuratiert, läuft sicher, und sieht nah an
// MapOut aus (clean OSM, gelbe/orange Straßenhierarchie, dezente Farben).
//
// Später — wenn wir eigene PMTiles generieren — ersetzen wir diesen Style
// durch einen selbstgebauten der offline läuft und echte SRTM-basierte
// Höhenlinien enthält (das geht sauber mit lokalen Daten).
// ---------------------------------------------------------------------
export function buildMapoutStyle(): string {
  return 'https://tiles.openfreemap.org/styles/liberty';
}

// ---------------------------------------------------------------------
// Fallback-Styles (solange keine eigenen PMTiles generiert sind).
//
// Drei Optionen, jede mit anderer Cartography-Philosophie:
//   - outdoor: CyclOSM — Radler-optimiert, mit Topo-Linien, weniger busy
//              als OpenTopoMap, Trails deutlich sichtbar
//   - minimal: CartoDB Voyager — super clean, muted Farben, wie MapOut-Look,
//              keine Höhenlinien (die kommen dann mit PMTiles wieder)
//   - satellite: Esri World Imagery
// ---------------------------------------------------------------------
export type FallbackStyleName = 'mapout' | 'outdoor' | 'minimal' | 'satellite';

export function buildFallbackRasterStyle(
  name: Exclude<FallbackStyleName, 'mapout'> = 'minimal',
): StyleSpecification {
  const styles: Record<Exclude<FallbackStyleName, 'mapout'>, { tiles: string[]; maxzoom: number; attribution: string; bgColor: string }> = {
    outdoor: {
      // CyclOSM — fahrrad-optimierte OSM-Darstellung mit dezenten Topo-Linien.
      // Deutlich weniger visueller Lärm als OpenTopoMap.
      tiles: [
        'https://a.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
        'https://b.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
        'https://c.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
      ],
      maxzoom: 18,
      attribution: 'Map data © OpenStreetMap contributors · Style: CyclOSM',
      bgColor: '#f1eee8',
    },
    minimal: {
      // CartoDB Voyager — sehr cleane, muted Cartography. Kein Topo, dafür
      // Trails maximal sichtbar. Eher MapOut-Ästhetik.
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
        'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
      ],
      maxzoom: 19,
      attribution: '© OpenStreetMap contributors · © CARTO',
      bgColor: '#f5f3ee',
    },
    satellite: {
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      maxzoom: 19,
      attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
      bgColor: '#1a2030',
    },
  };
  const cfg = styles[name];

  return {
    version: 8,
    // Fonts für Trail-Name-Labels. OpenFreeMap's Fonts-Endpoint ist stabil
    // und CORS-fähig — 'fonts.openmaptiles.org' war historisch oft down /
    // lieferte HTML-Errorpages zurück was dann den pbf-Parser mit
    // "Unimplemented type: 4" abstürzen lässt.
    // Wave 14ba: Glyphs vom EIGENEN Origin laden (Backend proxied + cached
    // zu openfreemap.org, siehe routes/fonts.js). Damit ist die Kartenschrift
    // offline-autark — der Service-Worker cached /fonts/... im mapfonts-v1
    // Cache, und selbst beim ersten Kaltstart nach Refresh sieht MapLibre
    // die gleiche Same-Origin-URL wie alle anderen Assets.
    glyphs: '/fonts/{fontstack}/{range}.pbf',
    sources: {
      base: {
        type: 'raster',
        tiles: cfg.tiles,
        tileSize: 256,
        attribution: cfg.attribution,
        maxzoom: cfg.maxzoom,
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': cfg.bgColor } },
      { id: 'base', type: 'raster', source: 'base' },
    ],
  };
}

export interface StyleOptions {
  mapPmtilesUrl: string;                 // z.B. "pmtiles:///tiles/liguria.pmtiles"
  contourPmtilesUrl?: string;            // optional, zeigt Höhenlinien wenn gesetzt
  hillshade?: boolean;                   // Hillshade-Layer aktivieren (braucht DEM-Source)
  hillshadePmtilesUrl?: string;          // lokale DEM-PMTiles (mapbox-encoded RGB). Wenn fehlt → AWS-Fallback
}

// Fallback: AWS Public Dataset "elevation-tiles-prod" mit Terrarium-encoded
// Höhenwerten (CC0). Wird nur genutzt wenn kein lokales hillshade-PMTiles
// verfügbar ist — das hier funktioniert NICHT offline.
const AWS_TERRARIUM_TILES = [
  'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
];

export function buildLiguriaStyle(opts: StyleOptions): StyleSpecification {
  const sources: StyleSpecification['sources'] = {
    basemap: {
      type: 'vector',
      url: opts.mapPmtilesUrl,
      attribution: '© OpenStreetMap contributors · OpenMapTiles',
    },
  };
  if (opts.contourPmtilesUrl) {
    sources.contours = {
      type: 'vector',
      url: opts.contourPmtilesUrl,
      attribution: '© SRTM · NASA',
    };
  }
  if (opts.hillshade) {
    if (opts.hillshadePmtilesUrl) {
      // Lokales DEM via PMTiles — funktioniert offline.
      // rio-rgbify generiert mit Default-Params 'mapbox'-Encoding,
      // d.h. Höhe = -10000 + ((R*65536 + G*256 + B) * 0.1).
      sources.dem = {
        type: 'raster-dem',
        url: opts.hillshadePmtilesUrl,
        tileSize: 256,
        encoding: 'mapbox',
        attribution: 'Terrain: SRTM (NASA)',
      };
    } else {
      // Online-Fallback: AWS Terrarium. Funktioniert nur mit Netz.
      sources.dem = {
        type: 'raster-dem',
        tiles: AWS_TERRARIUM_TILES,
        tileSize: 256,
        encoding: 'terrarium',
        maxzoom: 15,
        attribution: 'Terrain: Mapzen Terrarium (AWS Open Data)',
      };
    }
  }

  const contourLayers: StyleSpecification['layers'] = opts.contourPmtilesUrl
    ? [
        // Dünne Nebenlinien (alle 20m, außer Vielfache von 100)
        // Minzoom 9 (vorher 11): sobald die Region-PMTiles auch Tiles bei
        // z=9-10 enthalten (siehe REGION_CONTOUR_MINZOOMS in
        // generate-pmtiles.sh), rendern wir die Linien auch dort. Liguria
        // hat dort weiterhin keine Tiles → leere Renders, kein Clutter.
        // Everest hat z=9+ → Easter-Egg zeigt Höhenlinien sofort beim
        // Auto-Fit-Zoom (~9.5).
        {
          id: 'contour-minor',
          type: 'line',
          source: 'contours',
          'source-layer': 'contour',
          minzoom: 9,
          filter: ['!=', ['%', ['get', 'height'], 100], 0],
          paint: {
            'line-color': 'rgba(120, 85, 45, 0.35)',
            'line-width': 0.35,
          },
        },
        // Dicke Hauptlinien (alle 100m) — beschriftet
        {
          id: 'contour-major',
          type: 'line',
          source: 'contours',
          'source-layer': 'contour',
          minzoom: 9,
          filter: ['==', ['%', ['get', 'height'], 100], 0],
          paint: {
            'line-color': 'rgba(100, 70, 35, 0.55)',
            'line-width': 0.9,
          },
        },
        // Höhen-Labels entlang der Hauptlinien
        {
          id: 'contour-label',
          type: 'symbol',
          source: 'contours',
          'source-layer': 'contour',
          minzoom: 13,
          filter: ['==', ['%', ['get', 'height'], 100], 0],
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 200,
            'text-field': ['concat', ['to-string', ['get', 'height']], ' m'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 10,
            'text-max-angle': 25,
          },
          paint: {
            'text-color': '#705030',
            'text-halo-color': 'rgba(243, 237, 224, 0.85)',
            'text-halo-width': 1.5,
          },
        },
      ]
    : [];

  const layers: StyleSpecification['layers'] = [
    // Papier-cremiger Hintergrund
    { id: 'background', type: 'background', paint: { 'background-color': '#f3ede0' } },

    // Landcover: Wald / Forest — dezentes Olivgrün
    {
      id: 'landcover-wood',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'wood', 'forest'],
      paint: { 'fill-color': '#bccb9a', 'fill-opacity': 0.5 },
    },
    // Landcover: Grass / Meadow
    {
      id: 'landcover-grass',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'grass', 'meadow'],
      paint: { 'fill-color': '#d8deb2', 'fill-opacity': 0.4 },
    },
    // Landuse: Wohngebiete — fast unmerkbar beige
    {
      id: 'landuse-residential',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landuse',
      filter: ['==', 'class', 'residential'],
      paint: { 'fill-color': '#ebe3d0', 'fill-opacity': 0.5 },
    },

    // Wasser — sanftes Blau
    {
      id: 'water',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'water',
      paint: { 'fill-color': '#a9cfe3' },
    },

    // Hillshade unter den Contours: warme Schatten + helle Highlights →
    // 3D-Eindruck wie auf Topo-Karten / Caltopo. Exaggeration bewusst dezent
    // (0.45) damit's nicht "fake" wirkt. Akzentfarbe Richtung Erdton damit
    // sich's mit der creme-papier-Optik verträgt.
    ...(opts.hillshade
      ? ([
          {
            id: 'hillshade',
            type: 'hillshade',
            source: 'dem',
            paint: {
              'hillshade-exaggeration': 0.45,
              'hillshade-shadow-color': '#5a3f24',
              'hillshade-highlight-color': '#fffaf0',
              'hillshade-accent-color': '#704530',
              'hillshade-illumination-direction': 315,
              'hillshade-illumination-anchor': 'viewport',
            },
          },
        ] as StyleSpecification['layers'])
      : []),

    // Höhenlinien zwischen Hillshade und Straßen, damit die Straßen sie überlagern
    ...contourLayers,

    // Paths & Tracks — dünn, gestrichelt
    {
      id: 'road-path',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      minzoom: 12,
      filter: ['in', 'class', 'path', 'track'],
      paint: {
        'line-color': '#9c8970',
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 16, 1.8],
        'line-dasharray': [2, 2],
      },
    },
    // Minor / Service Straßen — weiß
    {
      id: 'road-minor',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'minor', 'service'],
      paint: {
        'line-color': '#ffffff',
        'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 3],
      },
    },
    // Tertiary — weiß mit beiger Case
    {
      id: 'road-tertiary-case',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      paint: {
        'line-color': '#d4c8a8',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 16, 6],
      },
    },
    {
      id: 'road-tertiary',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['==', 'class', 'tertiary'],
      paint: {
        'line-color': '#ffffff',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 16, 4.5],
      },
    },
    // Primary / Secondary — gelb mit oranger Case
    {
      id: 'road-primary-case',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      paint: {
        'line-color': '#c79a4a',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 16, 8],
      },
    },
    {
      id: 'road-primary',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'primary', 'secondary'],
      paint: {
        'line-color': '#f3c876',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 16, 6.5],
      },
    },
    // Motorway / Trunk — orange
    {
      id: 'road-motorway-case',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      paint: {
        'line-color': '#d87a35',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 16, 10],
      },
    },
    {
      id: 'road-motorway',
      type: 'line',
      source: 'basemap',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk'],
      paint: {
        'line-color': '#f7b27a',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 16, 8],
      },
    },

    // Ortsnamen — dezent dunkelbraun mit cremigem Halo
    {
      id: 'place-label',
      type: 'symbol',
      source: 'basemap',
      'source-layer': 'place',
      filter: ['in', 'class', 'city', 'town', 'village', 'hamlet'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 4, 9, 10, 12, 14, 15],
        'text-letter-spacing': 0.02,
      },
      paint: {
        'text-color': '#4a3828',
        'text-halo-color': 'rgba(243, 237, 224, 0.95)',
        'text-halo-width': 1.8,
      },
    },
  ];

  return {
    version: 8,
    // Wave 14ba: Glyphs vom EIGENEN Origin laden (Backend proxied + cached
    // zu openfreemap.org, siehe routes/fonts.js). Damit ist die Kartenschrift
    // offline-autark — der Service-Worker cached /fonts/... im mapfonts-v1
    // Cache, und selbst beim ersten Kaltstart nach Refresh sieht MapLibre
    // die gleiche Same-Origin-URL wie alle anderen Assets.
    glyphs: '/fonts/{fontstack}/{range}.pbf',
    sources,
    layers,
  };
}

// ---------------------------------------------------------------------
// "Minimal"-Style auf PMTiles-Basis.
//
// Reduziert den Liguria-Style auf das absolute Minimum:
//   - Keine Contours, kein Hillshade
//   - Keine Landcover-Füllung (außer Wasser, weil sonst verwirrend)
//   - Einheitlich graue Straßen-Hierarchie (kein Gelb-Orange)
//   - Keine Label außer Ortsnamen (ab Zoom 11)
//
// Sinn: clean "Trails-First"-Ansicht wo die farbigen Trail-Linien
// maximal hervorstechen und nichts vom Look ablenkt.
// ---------------------------------------------------------------------
export function buildMinimalLiguriaStyle(opts: { mapPmtilesUrl: string }): StyleSpecification {
  return {
    version: 8,
    // Wave 14ba: Glyphs vom EIGENEN Origin laden (Backend proxied + cached
    // zu openfreemap.org, siehe routes/fonts.js). Damit ist die Kartenschrift
    // offline-autark — der Service-Worker cached /fonts/... im mapfonts-v1
    // Cache, und selbst beim ersten Kaltstart nach Refresh sieht MapLibre
    // die gleiche Same-Origin-URL wie alle anderen Assets.
    glyphs: '/fonts/{fontstack}/{range}.pbf',
    sources: {
      basemap: {
        type: 'vector',
        url: opts.mapPmtilesUrl,
        attribution: '© OpenStreetMap contributors · OpenMapTiles',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#f7f4ec' } },
      // Wasser behalten — sonst sieht der Küstenverlauf komplett weggelassen aus
      {
        id: 'water',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'water',
        paint: { 'fill-color': '#d6e5ed' },
      },
      // Straßen einheitlich hellgrau, nur Breitenabstufung nach Klasse
      {
        id: 'road-all',
        type: 'line',
        source: 'basemap',
        'source-layer': 'transportation',
        minzoom: 9,
        filter: ['!=', 'class', 'path'],
        paint: {
          'line-color': '#cfc9bd',
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            9, ['match', ['get', 'class'], ['motorway', 'trunk'], 1.2, ['primary', 'secondary'], 0.8, 0.3],
            16, ['match', ['get', 'class'], ['motorway', 'trunk'], 6, ['primary', 'secondary'], 4, 1.8],
          ],
        },
      },
      // Ortsnamen minimal — erst ab Zoom 11, dezent
      {
        id: 'place-label',
        type: 'symbol',
        source: 'basemap',
        'source-layer': 'place',
        minzoom: 11,
        filter: ['in', 'class', 'city', 'town', 'village'],
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 13],
          'text-letter-spacing': 0.05,
          'text-transform': 'uppercase',
        },
        paint: {
          'text-color': '#7a7265',
          'text-halo-color': 'rgba(247, 244, 236, 0.95)',
          'text-halo-width': 1.5,
        },
      },
    ],
  };
}

// Trail-Rendering à la Trailforks — Wave 14be: SINGLE-SOURCE-REFACTOR.
//
// Vorher (bis 14bd): pro Trail eine eigene GeoJSON-Source + 5–6 eigene Layer
// (shadow/halo/[undercoat]/line/label/hit). Bei 28 Trails → 28 Sources +
// ~140 Layer. Pro Trail ein eigener map.on('click', ...)-Listener der beim
// Trail-Wechsel nicht abgeräumt wurde. Diagnose: "mit 3 Trails läuft alles
// perfekt, mit 28 Trails kriecht die Map". Skaliert katastrophal auf 200+.
//
// Jetzt (14be): EINE gemeinsame Source 'trails' mit FeatureCollection über
// ALLE Trails, plus eine FESTE Menge von Layern — unabhängig von der Anzahl
// Trails. Styling läuft 100% über Feature-Properties (`['get', '...']`).
// Ein einziger Click-Listener auf 'trails-hit' liest die Trail-ID aus
// `e.features[0].properties.id`.
//
// Layer-Stack (von unten nach oben):
//   1. trails-shadow       — dunkle, breite, geblurrte Linie (Depth)
//   2. trails-halo         — weiße Linie (Trailforks-"Pop"-Effekt)
//   3. trails-undercoat    — rote Undercoat, NUR für Features mit
//                            `hasUndercoat === true` (intermediate_plus)
//   4. trails-line-solid   — farbige Hauptlinie, filter: `isDashed === false`
//   5. trails-line-dashed  — farbige Hauptlinie dashed, filter: `isDashed === true`
//                            (MapLibre erlaubt `line-dasharray` NICHT data-
//                            driven → zwei getrennte Layer mit Filter)
//   6. trails-label        — Trail-Name entlang der Linie (ab Zoom 13)
//   7. trails-hit          — unsichtbare breite Linie für großzügige Taps;
//                            einziger Listener-Target (Click/Hover)
//
// Feature-Properties die MapView setzen MUSS (siehe buildTrailFeature dort):
//   id            — Trail-ID, für onTrailClick-Lookup
//   name          — für das Label
//   lineColor     — resolved color (colorOverride ODER DIFFICULTY_COLOR,
//                   ODER '#111111' für extreme ODER '#1565c0' für
//                   intermediate_plus)
//   labelColor    — meistens = lineColor; für striped-Fälle identisch zur
//                   dominanten Stripe-Farbe
//   isDashed      — true bei extreme/intermediate_plus ohne Override
//   hasUndercoat  — true NUR bei intermediate_plus ohne Override
//
// Namensraum-Hinweis: alle IDs starten mit `trails-` (Plural). Das schützt
// den Selection-Overlay (`trail-selection-*`, Singular) davor, von
// Namespace-Checks wie `startsWith('trails-')` mit-gelöscht zu werden.
const TRAILS_SOURCE_ID = 'trails';

const TRAIL_LAYOUT_ROUND = { 'line-cap': 'round' as const, 'line-join': 'round' as const };
// 'butt' statt 'round' damit die Rundungen die Dash-Gaps nicht "schließen".
// 2:1.5 = ~60% on / 40% off — liest sich auf Zoom 12–16 gut als gestrichelt.
const TRAIL_LAYOUT_DASHED = { 'line-cap': 'butt' as const, 'line-join': 'round' as const };

export function trailLayerSpec() {
  return [
    // 1. Schatten — breit, leicht geblurrt, halbtransparent
    {
      id: 'trails-shadow',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      paint: {
        'line-color': '#000000',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 12, 9, 16, 14],
        'line-opacity': 0.25,
        'line-blur': 2,
      },
      layout: TRAIL_LAYOUT_ROUND,
    },
    // 2. Weiße Halo
    {
      id: 'trails-halo',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      paint: {
        'line-color': '#ffffff',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 3.5, 12, 6.5, 16, 10.5],
        'line-opacity': 0.95,
      },
      layout: TRAIL_LAYOUT_ROUND,
    },
    // 3. Rote Undercoat — NUR intermediate_plus (filter per Property).
    //    Sitzt zwischen Halo (weiß) und dashed Main-Line (blau), damit in
    //    den Gaps der blauen Linie das rote Undercoat durchschimmert →
    //    blau/rot-Stripe-Effekt.
    {
      id: 'trails-undercoat',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      filter: ['==', ['get', 'hasUndercoat'], true],
      paint: {
        'line-color': '#d32f2f', // hard-rot
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 7],
      },
      layout: TRAIL_LAYOUT_ROUND,
    },
    // 4. Main-Line SOLID — alle Trails außer dashed (extreme/intermediate_plus
    //    ohne Override). `line-color` ist data-driven via Feature-Property.
    {
      id: 'trails-line-solid',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      filter: ['==', ['get', 'isDashed'], false],
      paint: {
        'line-color': ['get', 'lineColor'],
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 7],
      },
      layout: TRAIL_LAYOUT_ROUND,
    },
    // 5. Main-Line DASHED — getrennter Layer weil MapLibre `line-dasharray`
    //    nicht data-driven pro Feature erlaubt (nur zoom-abhängig). Filter
    //    macht die Feature-Auswahl.
    {
      id: 'trails-line-dashed',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      filter: ['==', ['get', 'isDashed'], true],
      paint: {
        'line-color': ['get', 'lineColor'],
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 7],
        'line-dasharray': [2, 1.5],
      },
      layout: TRAIL_LAYOUT_DASHED,
    },
    // 6. Labels (Name entlang der Linie, ab Zoom 13)
    {
      id: 'trails-label',
      type: 'symbol' as const,
      source: TRAILS_SOURCE_ID,
      minzoom: 13,
      layout: {
        'symbol-placement': 'line' as const,
        'symbol-spacing': 400,
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 16, 13],
        'text-letter-spacing': 0.02,
        'text-max-angle': 30,
      },
      paint: {
        'text-color': ['get', 'labelColor'],
        'text-halo-color': '#ffffff',
        'text-halo-width': 2.5,
        'text-halo-blur': 0.5,
      },
    },
    // 7. Hit-Line — unsichtbare breite Linie, obenauf für großzügige Taps.
    //    Einziger Listener-Target in MapView.
    {
      id: 'trails-hit',
      type: 'line' as const,
      source: TRAILS_SOURCE_ID,
      paint: {
        'line-color': '#000000',
        'line-opacity': 0,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 20, 12, 28, 16, 40],
      },
      layout: TRAIL_LAYOUT_ROUND,
    },
  ];
}

export const TRAILS_SOURCE_NAME = TRAILS_SOURCE_ID;
export const TRAILS_HIT_LAYER_ID = 'trails-hit';

// ─────────────────────────────────────────────────────────────────────────
// ULTRA: Renn-Route (3 Peaks Bike Race) + Offline-Korridor
//
// Zwei statische GeoJSON-Sources (corridorData/routeData), die EINMAL gesetzt
// und nie geändert werden. Der Korridor (60 km breit) zeigt, welcher Bereich
// offline verfügbar sein wird; die Route ist die durchgehende Renn-Linie über
// alle 9 Etappen. Bewusst dezent gehalten (Korridor blass), damit die Karte
// darunter lesbar bleibt.
// ─────────────────────────────────────────────────────────────────────────
export const CORRIDOR_SOURCE_ID = 'ultra-corridor';
export const ROUTE_SOURCE_ID = 'ultra-route';
export const NAVI_SEGMENTS_SOURCE_ID = 'ultra-navi-segments';

const ROUTE_LAYOUT_ROUND = { 'line-cap': 'round' as const, 'line-join': 'round' as const };

export function routeLayerSpec() {
  // ULTRA: Es wird NUR der Korridor-Umriss gezeichnet — KEINE vorgebackene
  // Renn-Route mehr. Der User will ausschließlich die GPX sehen, die er selbst
  // importiert (er passt die Strecke ggf. noch an). Die alten Route-Layer
  // (ultra-route-halo/-line/-label aus ROUTE_SOURCE_ID) sind entfernt.
  return [
    // Korridor-Fläche — sehr blass, nur als „das ist offline"-Hinweis.
    {
      id: 'ultra-corridor-fill',
      type: 'fill' as const,
      source: CORRIDOR_SOURCE_ID,
      paint: {
        'fill-color': '#2563eb',
        'fill-opacity': 0.07,
      },
    },
    // Korridor-Kante — dünn gestrichelt.
    {
      id: 'ultra-corridor-outline',
      type: 'line' as const,
      source: CORRIDOR_SOURCE_ID,
      paint: {
        'line-color': '#2563eb',
        'line-width': 1.5,
        'line-opacity': 0.45,
        'line-dasharray': [3, 2],
      },
      layout: ROUTE_LAYOUT_ROUND,
    },
    // Routing-Bereichsgrenzen: pro Bereich ein farbiger Umriss + Label, damit
    // der User nicht versehentlich über eine Bereichsgrenze plant (Routing
    // läuft nur innerhalb eines Bereichs). Farbe data-driven nach idx.
    {
      id: 'ultra-navi-seg-outline',
      type: 'line' as const,
      source: NAVI_SEGMENTS_SOURCE_ID,
      paint: {
        'line-color': [
          'match',
          ['get', 'idx'],
          1, '#e11d48',
          2, '#f59e0b',
          3, '#16a34a',
          4, '#2563eb',
          5, '#9333ea',
          '#666666',
        ],
        'line-width': 2,
        'line-opacity': 0.8,
        'line-dasharray': [2, 2],
      },
      layout: ROUTE_LAYOUT_ROUND,
    },
    {
      id: 'ultra-navi-seg-label',
      type: 'symbol' as const,
      source: NAVI_SEGMENTS_SOURCE_ID,
      layout: {
        'symbol-placement': 'line' as const,
        'symbol-spacing': 500,
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 12, 14],
        'text-max-angle': 30,
      },
      paint: {
        'text-color': [
          'match',
          ['get', 'idx'],
          1, '#e11d48',
          2, '#b45309',
          3, '#15803d',
          4, '#1d4ed8',
          5, '#7e22ce',
          '#444444',
        ],
        'text-halo-color': '#ffffff',
        'text-halo-width': 2.5,
      },
    },
  ];
}

/**
 * Resolved-Styling-Helper für EIN Trail-Feature. Wird in MapView von
 * buildTrailFeature aufgerufen und dort direkt in die Feature-Properties
 * gepackt. Zentral, damit Difficulty-Sonderfälle (extreme/intermediate_plus)
 * nicht in MapView dupliziert werden.
 */
export function resolveTrailStyle(
  difficulty: Difficulty,
  colorOverride?: string | null,
): { lineColor: string; labelColor: string; isDashed: boolean; hasUndercoat: boolean } {
  const isExtreme = difficulty === 'extreme' && !colorOverride;
  const isIntermediatePlus = difficulty === 'intermediate_plus' && !colorOverride;
  // baseColor = die "natürliche" Difficulty-Farbe (oder Admin-Override).
  // Diese wird für's Label benutzt, exakt wie in der Vor-14be-Implementation:
  // bei intermediate_plus ist das rot (DIFFICULTY_COLOR.intermediate_plus),
  // NICHT blau — das Label soll lesbar bleiben, Stripe-Semantik ist nur
  // auf der Linie relevant.
  const baseColor = colorOverride || DIFFICULTY_COLOR[difficulty];
  // Die sichtbare Haupt-Linie wird für striped-Varianten überschrieben:
  //   extreme           → schwarz, dashed → in den Gaps scheint weiße Halo
  //   intermediate_plus → blau,    dashed → in den Gaps scheint rote Undercoat
  const lineColor = isExtreme
    ? '#111111'
    : isIntermediatePlus
      ? '#1565c0' // intermediate-blau (dominante Stripe-Farbe)
      : baseColor;
  return {
    lineColor,
    labelColor: baseColor,
    isDashed: isExtreme || isIntermediatePlus,
    hasUndercoat: isIntermediatePlus,
  };
}
